// src/services/groupAdmin.js — group settings, permissions and admins, written
// with the app's EXACT Firestore shapes and system-message plumbing.
//
// Every action here does the same four things the phone does
// (GroupPermissionsModal.handlePermissionChange / ChatInfoScreen.handleUpdate*):
//
//   1. write the chat-doc field           (permissions.x / name / description / …)
//   2. add a `system` message doc         (type+systemType+sysKind+sysParams+text)
//   3. stamp the chat doc                 (lastMessage, lastMessageSystem,
//                                          lastMessageSenderId, timestamp, unreadCounts++)
//   4. relay over the socket              → live delivery to every member AND the
//                                           server's FCM push to their phones
//
// (3) is what makes the row appear in the chat LIST — on the phones and here —
// and (4) is what makes their phones buzz. Miss either and the change is silent.

import {
  doc, collection, addDoc, updateDoc, arrayUnion, arrayRemove,
  serverTimestamp, deleteField, writeBatch, increment,
} from 'firebase/firestore';
import { adminsAfterLeave, successorFor } from './groupLeave';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { getSocket } from './socket';

/* ── the app's defaults: ABSENT MEANS ALLOWED, except approveMembers ───────── */
export const DEFAULT_PERMS = {
  editSettings: true,
  sendMessages: true,
  addMembers: true,
  approveMembers: false,
  makeCalls: true,
};
export const permOf = (chat, key) => (key === 'approveMembers'
  ? chat?.permissions?.approveMembers === true
  : chat?.permissions?.[key] !== false);

export const isGroupAdmin = (chat, uid) => Array.isArray(chat?.admins) && chat.admins.includes(uid);
export const activeMembers = (chat) => {
  const left = new Set(chat?.leftParticipants || []);
  return (chat?.participants || []).filter((u) => u && !left.has(u));
};
/** Who may edit the name / description / photo. */
export const canEditInfo = (chat, uid) => isGroupAdmin(chat, uid) || permOf(chat, 'editSettings');

/* ── 🏷 MEMBER TAGS ───────────────────────────────────────────────────────────
   A role, title or nickname shown beside a member's name. Stored on the chat
   document as `participantTags: { [uid]: string }` — the same flat map the
   phone writes, so a tag added here is on their phone at its next snapshot and
   vice versa. Chat-wide, not per-viewer: one tag, everybody sees it.        */

/** The tag on a member, or '' — never undefined, so callers can just test it. */
export const tagOf = (chat, uid) => String(chat?.participantTags?.[uid] || '').trim();

/** 🟢 The phone's rule, mirrored: anyone may tag THEMSELVES, admins may tag anyone. */
export const canTagMember = (chat, meUid, uid) => uid === meUid || isGroupAdmin(chat, meUid);

export const TAG_MAX = 30;
export const canAddMembers = (chat, uid) => isGroupAdmin(chat, uid) || permOf(chat, 'addMembers');

/* ── the shared write: system doc + chat stamp + socket relay ──────────────── */
async function announce(chat, me, {
  text, systemType, sysKind, sysParams, extraMsg = {}, extraChat = {},
  senderIdOverride = null, senderNameOverride = null,
}) {
  const chatRef = doc(db, 'chats', chat.id);
  const senderId = senderIdOverride || me.uid;
  const senderName = senderNameOverride || me.name;

  // 1 ▸ the system message everyone renders
  const msgRef = await addDoc(collection(db, 'chats', chat.id, 'messages'), {
    type: 'system',
    text,
    systemType,
    sysKind,
    sysParams,
    senderId,
    senderName,
    timestamp: serverTimestamp(),
    ...extraMsg,
  });

  // 2 ▸ the chat-list row + everyone else's unread badge — as increments (the whole map read and
  //     written back lost a message's bump landing in between)
  const bumps = {};
  for (const uid of activeMembers(chat)) {
    if (uid !== me.uid) bumps[`unreadCounts.${uid}`] = increment(1);
  }
  await updateDoc(chatRef, {
    lastMessage: text,
    lastMessageSystem: {
      type: systemType, senderName, senderId, name: senderName, text, sysKind, sysParams,
    },
    lastMessageSenderId: senderId,
    timestamp: serverTimestamp(),
    ...bumps,
    ...extraChat,
  });

  // 3 ▸ live relay + the server's push to every member's phones
  const s = getSocket();
  if (s) {
    for (const uid of activeMembers(chat)) {
      if (uid === me.uid) continue;
      try {
        s.emit('new-message', {
          toUserId: uid,
          fromUserId: me.uid,
          message: text,
          chatId: chat.id,
          messageId: msgRef.id,
          isGroup: true,
          systemType,
          sysKind,
          sysParams: JSON.stringify(sysParams || {}),
          senderName,
        });
      } catch {}
    }
  }
  return msgRef.id;
}

/* ── permissions ──────────────────────────────────────────────────────────── */
const PERM_COPY = {
  sendMessages: {
    on: (n) => `${n} changed this group's settings to allow all members to send messages.`,
    off: (n) => `${n} changed this group's settings to allow only admins to send messages.`,
    kind: (v) => (v ? 'perm_msgs_all' : 'perm_msgs_admin'),
  },
  makeCalls: {
    on: (n) => `${n} changed this group's settings to allow all members to start calls.`,
    off: (n) => `${n} changed this group's settings to allow only admins to start calls.`,
    kind: (v) => (v ? 'perm_calls_all' : 'perm_calls_admin'),
  },
  editSettings: {
    on: (n) => `${n} changed group settings to allow members to edit group info.`,
    off: (n) => `${n} changed group settings to allow only admins to edit group info.`,
    kind: (v) => (v ? 'perm_edit_all' : 'perm_edit_admin'),
  },
  /* ⚙️ adding members changed silently on both clients — now it says so, like every other switch */
  addMembers: {
    on: (n) => `${n} changed this group's settings to allow all members to add others.`,
    off: (n) => `${n} changed this group's settings to allow only admins to add members.`,
    kind: (v) => (v ? 'perm_add_all' : 'perm_add_admin'),
  },
  approveMembers: {
    on: (n) => `${n} turned on admin approval to join this group.`,
    off: (n) => `${n} turned off admin approval to join this group.`,
    kind: (v) => (v ? 'perm_approve_on' : 'perm_approve_off'),
  },
};

/**
 * Flip a permission. `addMembers` has no system message on the phone either —
 * it just writes the flag — so this mirrors that silence rather than inventing
 * a sentence the phones would never produce.
 */
export async function setPermission(chat, me, key, value) {
  const chatRef = doc(db, 'chats', chat.id);
  const copy = PERM_COPY[key];
  if (!copy) {
    await updateDoc(chatRef, { [`permissions.${key}`]: value });
    return;
  }
  const text = value ? copy.on(me.name) : copy.off(me.name);
  await announce(chat, me, {
    text,
    systemType: 'permission_change',
    sysKind: copy.kind(value),
    sysParams: { name: me.name },
    extraMsg: { permissionType: key, permissionValue: value },
    /* 📣 WHEN only admins could send (the phones draw an admin's messages since then as announcements) */
    extraChat: { [`permissions.${key}`]: value, ...(key === 'sendMessages' ? { 'permissionsAt.sendMessages': serverTimestamp() } : {}) },
  });
}

/** Turning approval OFF with people waiting admits them all, exactly like the app. */
export async function disableApprovalAndAdmitAll(chat, me) {
  const pending = chat.pendingParticipants || [];
  if (pending.length) {
    const batch = writeBatch(db);
    const chatRef = doc(db, 'chats', chat.id);
    batch.update(chatRef, {
      'permissions.approveMembers': false,
      participants: arrayUnion(...pending),
      /* 🟥 `[]` deleted every request that arrived after this list was read — exactly these leave the queue */
      pendingParticipants: arrayRemove(...pending),
      leftParticipants: arrayRemove(...pending),
    });
    for (const uid of pending) {
      const name = chat.participantNames?.[uid] || 'A new member';
      batch.set(doc(collection(db, 'chats', chat.id, 'messages')), {
        type: 'system',
        systemType: 'user_joined',
        sysKind: 'user_joined',
        sysParams: { name },
        text: `${name} joined the group`,
        senderName: name,
        timestamp: serverTimestamp(),
        /* 🟥 written "as" the joiner — the rules let a message be written only by its own sender, so
              the whole batch (and the admit-all) was refused. 🟢 The admin writes it; the joiner is
              its subject. */
        senderId: me.uid,
        targetUid: uid,
      });
    }
    await batch.commit();
  }
  await setPermission(chat, me, 'approveMembers', false);
}

/* ── group info: name, description, photo ─────────────────────────────────── */
export async function setGroupName(chat, me, name) {
  const clean = String(name || '').trim();
  if (!clean || clean === chat.name) return;
  await announce(chat, me, {
    text: `${me.name} changed the group name to "${clean}"`,
    systemType: 'group_name_change',
    sysKind: 'name_change',
    sysParams: { name: me.name, groupName: clean },
    extraChat: { name: clean },
  });
}

export async function setGroupDescription(chat, me, description) {
  const clean = String(description || '').trim();
  if (clean === (chat.description || '')) return;
  await announce(chat, me, {
    text: `${me.name} changed the group description`,
    systemType: 'description_changed',
    sysKind: 'desc_changed',
    sysParams: { name: me.name },
    extraChat: { description: clean },
  });
}

/** Upload to the app's own storage path, then swap chats/{id}.groupImage. */
export async function setGroupImage(chat, me, file) {
  const path = `groupImages/${me.uid}/${Date.now()}.jpg`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file, { contentType: file.type || 'image/jpeg' });
  const url = await getDownloadURL(r);
  await announce(chat, me, {
    text: `${me.name} updated the group photo`,
    systemType: 'group_image_changed',
    sysKind: 'photo_updated',
    sysParams: { name: me.name },
    extraChat: { groupImage: url },
  });
  return url;
}

export async function removeGroupImage(chat, me) {
  await announce(chat, me, {
    text: `${me.name} removed the group photo`,
    systemType: 'group_image_changed',
    sysKind: 'photo_removed',
    sysParams: { name: me.name },
    // deleteField(), not '' — older clients read `groupImage` truthily
    extraChat: { groupImage: deleteField() },
  });
}

/* ── admins ───────────────────────────────────────────────────────────────── */
/**
 * Set (or, with an empty string, remove) a member's tag.
 *
 * 🟥 THE GATE IS CHECKED HERE, NOT ONLY ON THE BUTTON. Firestore's rules for
 *    chat documents allow any participant to write any field, so "only admins
 *    can tag other people" is a promise the UI makes and only the client can
 *    keep. Hiding the button is not the same as refusing the write — a stale
 *    render, or a second window open from before a demotion, would sail
 *    straight through.
 *
 * 🟢 No system message. The phone writes this silently too; a group of ten
 *    would otherwise get a notification every time somebody edited a nickname.
 */
export async function setMemberTag(chat, me, uid, tag) {
  if (!chat?.id || !uid) throw new Error('Missing chat or member.');
  if (!canTagMember(chat, me.uid, uid)) {
    throw new Error('Only an admin can change another member’s tag.');
  }
  /* trimmed and capped exactly as the phone's field does, so neither client can
     store something the other refuses to render */
  const clean = String(tag || '').trim().slice(0, TAG_MAX);
  await updateDoc(doc(db, 'chats', chat.id), { [`participantTags.${uid}`]: clean });
}

/** 👑 May `me` dismiss `target`? (the phone's services/groupPermissions.adminDismissal) */
export function adminDismissal(chat, meUid, target) {
  const admins = chat?.admins || [];
  if (!admins.includes(target)) return 'not_admin';
  if (chat?.createdBy && target === chat.createdBy && target !== meUid) return 'creator';
  const left = new Set(chat?.leftParticipants || []);
  const inGroup = new Set(chat?.participants || []);
  return admins.some((a) => a !== target && inGroup.has(a) && !left.has(a)) ? 'ok' : 'last';
}

export async function toggleAdmin(chat, me, memberId) {
  const wasAdmin = isGroupAdmin(chat, memberId);
  if (wasAdmin) {
    const v = adminDismissal(chat, me.uid, memberId);
    if (v === 'creator' || v === 'last') {
      const e = new Error(v);
      e.userMessage = v === 'creator' ? "The group's creator can't be dismissed as admin." : 'Make someone else an admin first — a group needs at least one admin.';
      throw e;
    }
  }
  const targetName = chat.participantNames?.[memberId] || 'Member';
  await updateDoc(doc(db, 'chats', chat.id), {
    admins: wasAdmin ? arrayRemove(memberId) : arrayUnion(memberId),
  });
  await announce(chat, me, {
    text: wasAdmin
      ? `${me.name} dismissed ${targetName} as admin`
      : `${me.name} promoted ${targetName} to admin`,
    systemType: wasAdmin ? 'admin_demoted' : 'admin_promoted',
    sysKind: wasAdmin ? 'admin_demoted' : 'admin_promoted',
    sysParams: { sender: me.name, target: targetName },
    extraMsg: { targetName },
  });
}

/* ── join requests ────────────────────────────────────────────────────────── */
export async function approveRequest(chat, me, uid) {
  const name = chat.participantNames?.[uid] || 'A new member';
  await updateDoc(doc(db, 'chats', chat.id), {
    participants: arrayUnion(uid),
    pendingParticipants: arrayRemove(uid),
    // a returning member's exit markers must go, or the chat stays hidden for them
    leftParticipants: arrayRemove(uid),
    [`leftAt.${uid}`]: deleteField(),
    [`deletedFor.${uid}`]: deleteField(),
    [`participantNames.${uid}`]: name,
    [`unreadCounts.${uid}`]: 0,
    [`joinedAt.${uid}`]: Date.now(),   // 👥 their history starts here
  });
  await announce(
    { ...chat, participants: [...(chat.participants || []), uid] },
    me,
    {
      text: `${name} joined the group`,
      systemType: 'user_joined',
      sysKind: 'user_joined',
      sysParams: { name },
      /* 🟥 the row was written "as" the joiner — refused by the rules (a message is written by its own
            sender), so the approve looked failed. 🟢 The admin writes it; the joiner is its subject. */
      extraMsg: { targetUid: uid },
    },
  );
}

export function declineRequest(chat, uid) {
  return updateDoc(doc(db, 'chats', chat.id), { pendingParticipants: arrayRemove(uid) });
}

/* ── leaving ──────────────────────────────────────────────────────────────── */
//
// 🟥 LEAVE IS NOT "DELETE & EXIT". They look similar and are deliberately
//    different writes, mirroring the two the app has:
//
//      ChatInfoScreen.leaveGroup   → leftParticipants + leftAt, and NOTHING else.
//                                    I stay in `participants`, the chat stays in
//                                    my list, the history stays readable — I'm
//                                    just out of the conversation.
//      ChatsTab.handleDeleteChat   → the above PLUS removing me from
//                                    `participants` and `deletedFor.{me}` = true,
//                                    which is what makes the row disappear.
//                                    (That one lives in services/chatActions.js.)
//
//    Writing the delete flavour here would silently destroy the chat someone
//    only meant to step out of, so the two stay apart.
//
// 🟥 AND `participants` MUST NOT SHRINK. Every chat query in both clients is
//    `where('participants','array-contains', uid)`. Dropping myself from it is
//    how the chat vanishes — correct for delete, wrong for leave. The
//    "am I still in?" question is answered by `leftParticipants`, which is why
//    that array exists at all.

/** Leave a group but keep the chat. Mirrors ChatInfoScreen.leaveGroup exactly. */
export async function leaveGroup(chat, me) {
  const chatRef = doc(db, 'chats', chat.id);

  // already out (left on the phone, or removed by an admin) → nothing to do
  if ((chat.leftParticipants || []).includes(me.uid)) return;

  // 1 ▸ the system row, the chat-list preview, everyone's unread and the push — FIRST, while I am
  //     still a member: a member who has left may no longer write the group's conversation state.
  const name = me.name || 'A member';
  await announce(chat, me, {
    text: `${name} left the group`,
    systemType: 'user_left',
    sysKind: 'user_left',
    sysParams: { name },
  });

  // 2 ▸ mark myself as left (leftAt is what the "you left this group" state reads) — and if I was
  //     the last admin, the role passes on (services/groupLeave) — and the group is TOLD who has it.
  const admins = adminsAfterLeave(chat, me.uid);
  const heir = successorFor(chat, me.uid);
  if (heir) {
    const heirName = String(chat.participantNames?.[heir] || '').trim() || 'A member';
    try {
      await addDoc(collection(db, 'chats', chat.id, 'messages'), {
        type: 'system', systemType: 'admin_promoted', sysKind: 'admin_auto', sysParams: { target: heirName },
        text: `${heirName} is now an admin`, senderId: me.uid, targetUid: heir, targetName: heirName, timestamp: serverTimestamp(),
      });
    } catch {}
  }
  await updateDoc(chatRef, {
    leftParticipants: arrayUnion(me.uid),
    [`leftAt.${me.uid}`]: serverTimestamp(),
    ...(admins ? { admins } : {}),
  });
}
